import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { usePremiumOffer } from '@src/pages/PremiumPage/PremiumOfferProvider';
import { isGameUpsellContext, trackPremium, type PremiumClickContext } from '@src/utils/premiumAnalytics';
import { isDatabaseUpsellContext, recordUpsellImpression } from '@src/utils/upsellImpressions';
import { observeOfferVisibility } from '@src/utils/observeOfferVisibility';

/** Wrap the price or priced CTA, never navigation or an entire tall panel. */
export function PremiumOfferView({ children, enabled = true, placement, feature, context, theme }: PremiumClickContext & {
  children: ReactNode; enabled?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const seen = useRef(new Set<string>());
  const offer = usePremiumOffer(feature);
  const { key: routeKey, pathname } = useLocation();
  const { ready, experiment, variant, amountMinor } = offer;
  useEffect(() => {
    const key = JSON.stringify([routeKey, experiment, variant, amountMinor, placement, feature, context, theme]);
    if (!enabled || !ready || !ref.current || typeof IntersectionObserver === 'undefined') return;
    const cleanup: (() => void)[] = [];
    const details = { placement, feature, context, theme, action: 'offer_viewed' };
    if (!seen.current.has(key)) {
      cleanup.push(observeOfferVisibility(ref.current, () => {
        seen.current.add(key);
        trackPremium('premium_offer_viewed_v2', details, offer);
      }));
    }
    // Observe the entire CTA, independently of the existing 50%-visible price.
    // Excluded routes never emit this event.
    // The Premium landing page's quoted offer text is an impression target on this page.
    const button = ref.current.closest<HTMLElement>('button, a')
      ?? (/^\/premium\/?$/.test(pathname) ? ref.current : null);
    const upsellKey = `upsell:${key}`;
    if (button && isDatabaseUpsellContext(pathname, context) && !seen.current.has(upsellKey)) {
      cleanup.push(observeOfferVisibility(button, () => {
        seen.current.add(upsellKey);
        recordUpsellImpression(pathname, details, offer);
        if (isGameUpsellContext(pathname, context)) trackPremium('premium_offer_upsell_viewed_v2', details, offer);
      }, 1));
    }
    return () => cleanup.forEach(stop => stop());
    // Scalar dependencies avoid restarting the timer on unrelated rerenders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ready, routeKey, pathname, experiment, variant, amountMinor, placement, feature, context, theme]);
  return <span ref={ref}>{children}</span>;
}
